import React, { useState, useEffect, useMemo } from "react";
import ComboBox from "./ComboBox";
import UploadImageModal from "./UploadImageModal";
import AthleteDeleteAccountModal from "./AthleteDeleteAccountModal";
import { uploadUserAvatar, clearMyAvatarCache, useMyAvatar } from "../utils/profilePictureUtils";
import { updateAthleteProfile, fetchSports, groupSportsByCategory, Sport } from "../utils/athleteApi";
import { isAthlete, setHasProfile, getTermsAccepted } from "../utils/auth";
import { buildApiUrl } from "../utils/apiConfig";
import {
    autoFormatDateInput,
    formatDateOfBirthUs,
    isIsoDateStrictlyBeforeToday,
    parseDateOfBirthInputToIso,
} from "../utils/dateDisplay";
import { lookupAddressFromZip, normalizeUsPostalDigits } from "../utils/addressApi";
import { SHOW_ATHLETE_PROFILE_ZIP_FIELD } from "../constants/athleteProfileFeatures";
import StateComboBox, { StateOption } from "./StateComboBox";
import CityComboBox, { City } from "./CityComboBox";
import { jsonGet } from "../utils/http";
import { useAuth } from "../contexts/AuthContext";
import "../styles/athlete-edit-form.css";
import "../styles/profile-form.css";
import { apiClient } from "../utils/apiClient";

interface AthleteProfileData {
    id?: string;
    firstName?: string;
    lastName?: string;
    nickname?: string;
    sport?: Sport | string;  // Can be Sport object or legacy string
    graduationYear?: string;
    school?: string;
    phone?: string;
    email?: string;
    parentEmail?: string;
    parentEmail2?: string;
    parentPhone?: string;
    dateOfBirth?: string;
    program?: string;
    location?: string;
    pronouns?: string;
    profileImageUrl?: string;
    imageUrl?: string;
    athletePhone?: string;
    parentMobile?: string;
    hudlUrl?: string;
    // For internal state management
    staffId?: number;
    staffName?: string;
    // State management
    stateId?: number;
    /** State name from address API / ZIP lookup (GET athletes may send stateName) */
    stateName?: string;
    // City management
    cityId?: number;
    cityName?: string;
    postalCode?: string;
    parentUserId?: number;
    cleverId?: string | null;
    cleverDistrictName?: string | null;
    authProvider?: string;
}

interface AthleteEditFormProps {
    athleteData: AthleteProfileData;
    onSave: () => Promise<void>;
    onCancel?: () => void;
    saving: boolean;
}

export default function AthleteEditForm({ athleteData, onSave, onCancel, saving }: AthleteEditFormProps) {
    const { authProvider } = useAuth();
    const isCleverAthlete = authProvider === "clever" && isAthlete();
    const { avatarUrl: myAvatarUrl } = useMyAvatar(isAthlete() && getTermsAccepted());
    const hasExplicitState = !!(athleteData.stateId && (athleteData.location || athleteData.stateName));
    const [editData, setEditData] = useState<AthleteProfileData>({
        ...athleteData,
        stateId: hasExplicitState ? athleteData.stateId : undefined,
        location: hasExplicitState ? (athleteData.location || athleteData.stateName) : undefined,
        postalCode: (() => {
            const n = normalizeUsPostalDigits(athleteData.postalCode || "");
            return n.length > 0 ? n : undefined;
        })(),
    });
    const [saveError, setSaveError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof AthleteProfileData, string>>>({});
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    /** Local preview for a newly selected file (not yet saved) */
    const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
    const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
    const [sports, setSports] = useState<Sport[]>([]);
    const sportGroups = useMemo(
        () => groupSportsByCategory(sports).map((group) => ({
            label: group.category,
            options: group.sports,
        })),
        [sports]
    );
    const [sportsLoading, setSportsLoading] = useState(true);
    const [sportsError, setSportsError] = useState<string | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [zipLookupLoading, setZipLookupLoading] = useState(false);
    const [zipLookupError, setZipLookupError] = useState<string | null>(null);
    const [stateOptions, setStateOptions] = useState<StateOption[]>([]);
    const [dateOfBirthText, setDateOfBirthText] = useState(
        () => formatDateOfBirthUs(athleteData.dateOfBirth) ?? ""
    );
    /** Normalized digits (5 or 9) last confirmed via Look up or loaded with city/state from API */
    const [confirmedPostalDigits, setConfirmedPostalDigits] = useState(() => {
        const z = normalizeUsPostalDigits(athleteData.postalCode);
        if (z.length >= 5 && athleteData.stateId != null && athleteData.cityId != null) {
            return z;
        }
        return "";
    });

    // Fetch sports on component mount
    useEffect(() => {
        const loadSports = async () => {
            setSportsLoading(true);
            setSportsError(null);
            try {
                const sportsData = await fetchSports();
                if (sportsData) {
                    setSports(sportsData);
                } else {
                    setSportsError("Failed to load sports");
                }
            } catch (err) {
                setSportsError("Failed to load sports");
            } finally {
                setSportsLoading(false);
            }
        };

        loadSports();
    }, []);

    useEffect(() => {
        if (SHOW_ATHLETE_PROFILE_ZIP_FIELD) {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const view = await jsonGet<{ states?: StateOption[] }>(
                    buildApiUrl("api/athletes/add/view"),
                    true
                );
                if (cancelled) {
                    return;
                }
                const states = Array.isArray(view?.states) ? view.states : [];
                setStateOptions(states.sort((a, b) => a.name.localeCompare(b.name)));
            } catch (err) {
                console.error("Failed to load states for athlete profile", err);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setDateOfBirthText(formatDateOfBirthUs(athleteData.dateOfBirth) ?? "");
    }, [athleteData.dateOfBirth]);

    const selectedCity = useMemo((): City | null => {
        if (editData.cityId == null || !editData.cityName?.trim() || editData.stateId == null) {
            return null;
        }
        return {
            id: editData.cityId,
            name: editData.cityName,
            stateId: editData.stateId,
            stateName: editData.location || editData.stateName || "",
        };
    }, [editData.cityId, editData.cityName, editData.stateId, editData.location, editData.stateName]);

    const handleFieldChange = (field: keyof AthleteProfileData, value: string) => {
        setEditData(prev => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const clearLocationFields = (): Pick<
        AthleteProfileData,
        "stateId" | "cityId" | "cityName" | "location" | "stateName"
    > => ({
        stateId: undefined,
        cityId: undefined,
        cityName: "",
        location: undefined,
        stateName: undefined,
    });

    const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
        const z = normalizeUsPostalDigits(digits);
        if (z.length === 0) {
            setConfirmedPostalDigits("");
            setEditData((prev) => ({
                ...prev,
                postalCode: "",
                ...clearLocationFields(),
            }));
            if (zipLookupError) setZipLookupError(null);
            if (fieldErrors.postalCode) {
                setFieldErrors((prev) => ({ ...prev, postalCode: undefined }));
            }
            return;
        }
        setEditData((prev) => {
            const next: AthleteProfileData = { ...prev, postalCode: digits };
            if (
                confirmedPostalDigits.length >= 5 &&
                z.slice(0, 5) !== confirmedPostalDigits.slice(0, 5)
            ) {
                return { ...next, ...clearLocationFields() };
            }
            return next;
        });
        if (zipLookupError) setZipLookupError(null);
        if (fieldErrors.postalCode) {
            setFieldErrors((prev) => ({ ...prev, postalCode: undefined }));
        }
    };

    const handleZipLookup = async () => {
        setZipLookupError(null);
        const zip = editData.postalCode?.trim() || "";
        setZipLookupLoading(true);
        try {
            const result = await lookupAddressFromZip(zip);
            if (!result.ok) {
                setZipLookupError(result.error);
                return;
            }
            const d = result.data;
            if (d.stateId == null || d.cityId == null) {
                setZipLookupError("Could not resolve city and state for this ZIP.");
                return;
            }
            const normalized = normalizeUsPostalDigits(d.postalCode ?? "");
            setConfirmedPostalDigits(normalized);
            setEditData((prev) => ({
                ...prev,
                postalCode: normalized,
                stateId: d.stateId ?? undefined,
                cityId: d.cityId ?? undefined,
                cityName: d.cityName || "",
                location: d.stateName ?? prev.location,
                stateName: d.stateName ?? prev.stateName,
            }));
            if (fieldErrors.postalCode) {
                setFieldErrors((prev) => ({ ...prev, postalCode: undefined }));
            }
        } finally {
            setZipLookupLoading(false);
        }
    };

    const handleSaveClick = async () => {
        setSaveError(null);

        // Client-side validation
        const errors: Partial<Record<keyof AthleteProfileData, string>> = {};
        let resolvedDobIso = "";

        if (!editData.firstName?.trim()) {
            errors.firstName = "First name is required.";
        }
        if (!editData.lastName?.trim()) {
            errors.lastName = "Last name is required.";
        }

        const dobTrim = dateOfBirthText.trim();
        if (dobTrim) {
            const parsed = parseDateOfBirthInputToIso(dobTrim);
            if (!parsed) {
                errors.dateOfBirth = "Enter date of birth as MM/DD/YYYY.";
            } else if (!isIsoDateStrictlyBeforeToday(parsed)) {
                errors.dateOfBirth = "Date of birth must be in the past.";
            } else {
                resolvedDobIso = parsed;
            }
        }

        if (SHOW_ATHLETE_PROFILE_ZIP_FIELD) {
            const zipNorm = normalizeUsPostalDigits(editData.postalCode);
            if (zipNorm.length > 0 && zipNorm.length < 5) {
                errors.postalCode = "Enter a valid 5-digit U.S. ZIP code (or ZIP+4).";
            } else if (zipNorm.length >= 5 && zipNorm !== confirmedPostalDigits) {
                errors.postalCode =
                    'ZIP changed; click "Look up" to confirm city and state before saving.';
            } else if (zipNorm.length >= 5 && (editData.stateId == null || editData.cityId == null)) {
                errors.postalCode = 'Click "Look up" to confirm your ZIP and set city and state.';
            }
        }

        const emailValue = editData.email?.trim() || "";
        if (!emailValue) {
            errors.email = "Email is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
            errors.email = "Please enter a valid email address.";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setFieldErrors({});

        try {
            // First, upload the profile picture if a new one was selected
            if (selectedImageFile) {
                setUploadingImage(true);
                try {
                    const result = await uploadUserAvatar(selectedImageFile);
                    if (!result.success) {
                        throw new Error(result.error || "Failed to upload profile picture");
                    }
                    clearMyAvatarCache();
                    window.dispatchEvent(new Event('athleteProfileUpdated'));
                    window.dispatchEvent(new CustomEvent('profilePictureUpdated'));
                } catch (err: any) {
                    setSaveError(err?.message || "Failed to upload profile picture");
                    setUploadingImage(false);
                    return; // Don't proceed with saving profile if image upload fails
                } finally {
                    setUploadingImage(false);
                }
            }


            // Helper function to get sport value for payload
            const getSportValue = () => {
                // Check editData.sport first
                if (editData.sport) {
                    if (typeof editData.sport === 'object' && 'id' in editData.sport) {
                        return editData.sport; // Return sport object with id and name
                    }
                    if (typeof editData.sport === 'string') {
                        return editData.sport; // Return string value
                    }
                }
                // Check athleteData.sport as fallback
                if (athleteData?.sport) {
                    if (typeof athleteData.sport === 'object' && 'id' in athleteData.sport) {
                        return athleteData.sport;
                    }
                    if (typeof athleteData.sport === 'string') {
                        return athleteData.sport;
                    }
                }
                // Return null instead of empty string when no sport is selected
                return null;
            };

            const zipForSave = normalizeUsPostalDigits(editData.postalCode);
            const clearingAddress = SHOW_ATHLETE_PROFILE_ZIP_FIELD
                ? zipForSave.length === 0
                : editData.stateId == null || editData.cityId == null;

            const finalStateId: number | undefined = clearingAddress
                ? undefined
                : editData.stateId || undefined;
            const finalStateName: string = clearingAddress
                ? ""
                : editData.location || editData.stateName || "";

            // Build the request payload according to API spec
            const payload = {
                firstName: editData.firstName || "",
                lastName: editData.lastName || "",
                email: editData.email || "",
                title: editData.nickname || "",
                state: finalStateName,
                stateId: clearingAddress ? null : finalStateId || null,
                cityId: clearingAddress ? null : editData.cityId,
                staffName: athleteData?.staffName || "",
                staffId: athleteData?.staffId,
                nickname: editData.nickname || "",
                graduationYear: editData.graduationYear ? parseInt(editData.graduationYear) : undefined,
                school: editData.school || "",
                program: editData.program || "",
                sport: getSportValue(),
                location: finalStateName,
                postalCode: SHOW_ATHLETE_PROFILE_ZIP_FIELD
                    ? (clearingAddress ? undefined : zipForSave)
                    : undefined,
                pronouns: editData.pronouns || "",
                dateOfBirth: resolvedDobIso,
                athletePhone: editData.phone || editData.athletePhone || "",
                parentEmail: editData.parentEmail || "",
                parentEmail2: editData.parentEmail2 || "",
                parentPhone: editData.parentPhone || editData.parentMobile || "",
                // For admin updates, parentUserId is the admin's userId from localStorage
                parentUserId: athleteData?.parentUserId || localStorage.getItem('userId'),
            };

            // Get athlete ID from the profile data or localStorage
            const athleteId = editData.id || athleteData.id || localStorage.getItem("athleteId");

            if (!athleteId) {
                throw new Error("Athlete ID is required to update profile");
            }

            // Use the updateAthleteProfile function with athlete ID
            const result = await updateAthleteProfile(athleteId, payload, isAthlete());

            if (result.success) {
                // Mark profile as complete
                setHasProfile(true);

                await onSave();
            } else {
                throw new Error(result.error || "Failed to save profile");
            }
        } catch (err: any) {
            console.error("Failed to save profile:", err);
            setSaveError(err?.message || "Failed to save profile");
        }
    };

    const handleUploadProfilePicture = async (file: File) => {
        if (!file) return;

        // Store the file and create a preview, but don't upload yet
        setSelectedImageFile(file);
        const previewUrl = URL.createObjectURL(file);
        setProfileImagePreview(previewUrl);
        setIsUploadModalOpen(false);
    };

    const handleDeleteAccount = async () => {
        setIsDeleting(true);
        setDeleteError(null);

        try {
            const apiUrl = buildApiUrl('api/athletes/me');
            const response = await apiClient(apiUrl, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (response.status === 204) {
                // Success - clear all auth data and redirect to login
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/login';
            } else if (response.status === 400) {
                setDeleteError('Unable to delete account. You may not have the correct permissions.');
                setIsDeleting(false);
            } else if (response.status === 401) {
                setDeleteError('You must be logged in to delete your account.');
                setIsDeleting(false);
            } else if (response.status === 404) {
                setDeleteError('Account not found.');
                setIsDeleting(false);
            } else {
                setDeleteError('Failed to delete account. Please try again.');
                setIsDeleting(false);
            }
        } catch (error) {
            setDeleteError('An unexpected error occurred. Please try again.');
            setIsDeleting(false);
        }
    };

    const displayProfileImage = profileImagePreview ?? myAvatarUrl ?? null;

    return (
        <div className="athlete-edit-form-wrapper">
            <div className="athlete-edit-form-header">
                <h1 className="athlete-edit-form-title">Edit athlete info</h1>
                <div className="athlete-edit-form-actions">
                    {onCancel ? (
                        <button
                            className="athlete-edit-cancel-button"
                            onClick={onCancel}
                            disabled={saving || uploadingImage}
                        >
                            Cancel
                        </button>
                    ) : null}
                    <button
                        className="athlete-edit-save-button"
                        onClick={handleSaveClick}
                        disabled={saving || uploadingImage}
                    >
                        {uploadingImage ? 'Uploading image...' : saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {saveError && (
                <div className="form-alert is-error">
                    {saveError}
                </div>
            )}

            {/* Profile Picture Section */}
            <div className="athlete-edit-picture-section">
                <div className="athlete-edit-picture-container">
                    <div className="athlete-edit-picture-preview">
                        {displayProfileImage ? (
                            <img src={displayProfileImage} alt="Profile" />
                        ) : (
                            <div className="athlete-edit-picture-placeholder"></div>
                        )}
                    </div>
                    <div className="athlete-edit-picture-controls">
                        <label className="athlete-edit-picture-label">PROFILE PICTURE</label>
                        <button
                            type="button"
                            className="athlete-edit-add-button"
                            onClick={() => setIsUploadModalOpen(true)}
                        >
                            <span className="athlete-edit-add-icon">+</span>
                            Add
                        </button>
                    </div>
                </div>
            </div>

            {/* Required Fields Label */}
            <div className="athlete-edit-required-label">*REQUIRED FIELDS</div>

            {/* Form Grid */}
            <div className="athlete-edit-form-grid">
                {/* Personal Information Section */}
                <div className="athlete-edit-section-content">
                    <div className="athlete-edit-section-header section-personal">
                        <span className="section-icon">👤</span>
                        <span>Personal Information</span>
                    </div>

                    {isCleverAthlete && (
                        <p className="clever-account-hint athlete-edit-clever-hint">
                            This Polaris account uses Clever sign-in. Your district name below helps confirm you are with
                            the correct school organization.
                        </p>
                    )}

                    <div className="athlete-edit-field">
                        <label>FIRST NAME*</label>
                        <input
                            type="text"
                            className={fieldErrors.firstName ? "input-error" : ""}
                            value={editData.firstName || ''}
                            onChange={(e) => handleFieldChange('firstName', e.target.value)}
                        />
                        {fieldErrors.firstName && <span className="field-error">{fieldErrors.firstName}</span>}
                    </div>

                    <div className="athlete-edit-field">
                        <label>LAST NAME*</label>
                        <input
                            type="text"
                            className={fieldErrors.lastName ? "input-error" : ""}
                            value={editData.lastName || ''}
                            onChange={(e) => handleFieldChange('lastName', e.target.value)}
                        />
                        {fieldErrors.lastName && <span className="field-error">{fieldErrors.lastName}</span>}
                    </div>

                    <div className="athlete-edit-field">
                        <label>DATE OF BIRTH</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="bday"
                            placeholder="MM/DD/YYYY"
                            className={fieldErrors.dateOfBirth ? "input-error" : ""}
                            value={dateOfBirthText}
                            onChange={(e) => {
                                setDateOfBirthText(autoFormatDateInput(e.target.value, dateOfBirthText));
                                if (fieldErrors.dateOfBirth) {
                                    setFieldErrors((prev) => ({ ...prev, dateOfBirth: undefined }));
                                }
                            }}
                            onBlur={() => {
                                const trimmed = dateOfBirthText.trim();
                                if (!trimmed) {
                                    setEditData((prev) => ({ ...prev, dateOfBirth: "" }));
                                    setDateOfBirthText("");
                                    return;
                                }
                                const iso = parseDateOfBirthInputToIso(trimmed);
                                if (iso) {
                                    setEditData((prev) => ({ ...prev, dateOfBirth: iso }));
                                    setDateOfBirthText(formatDateOfBirthUs(iso) ?? "");
                                }
                            }}
                        />
                        {fieldErrors.dateOfBirth && <span className="field-error">{fieldErrors.dateOfBirth}</span>}
                    </div>

                    <div className="athlete-edit-field">
                        <label>NICKNAME</label>
                        <input
                            type="text"
                            value={editData.nickname || ''}
                            onChange={(e) => handleFieldChange('nickname', e.target.value)}
                            placeholder="Optional"
                        />
                    </div>

                    {isCleverAthlete && (
                        <div className="athlete-edit-field athlete-edit-field--full">
                            <label>DISTRICT NAME</label>
                            <div className="athlete-edit-readonly-value" aria-readonly="true">
                                {athleteData.cleverDistrictName?.trim() ? athleteData.cleverDistrictName : '—'}
                            </div>
                        </div>
                    )}
                </div>

                {/* Location Section */}
                <div className="athlete-edit-section-content">
                    <div className="athlete-edit-section-header section-location">
                        <span className="section-icon">📍</span>
                        <span>Location</span>
                    </div>

                    <div className="athlete-edit-location-inner">
                        {SHOW_ATHLETE_PROFILE_ZIP_FIELD && (
                            <div className="athlete-edit-field athlete-edit-zip-field">
                                <label htmlFor="athlete-profile-zip">ZIP code</label>
                                <div className="athlete-edit-zip-inline">
                                    <input
                                        id="athlete-profile-zip"
                                        type="text"
                                        inputMode="text"
                                        autoComplete="postal-code"
                                        maxLength={14}
                                        className={fieldErrors.postalCode ? "input-error" : ""}
                                        placeholder="e.g. 90210 or 78665-2385"
                                        value={editData.postalCode || ""}
                                        onChange={handlePostalCodeChange}
                                    />
                                    <button
                                        type="button"
                                        className="athlete-edit-zip-lookup-btn"
                                        onClick={handleZipLookup}
                                        disabled={
                                            zipLookupLoading ||
                                            (editData.postalCode?.replace(/\D/g, "") || "").length < 5
                                        }
                                    >
                                        {zipLookupLoading ? "Looking up…" : "Look up"}
                                    </button>
                                </div>
                                <p className="athlete-edit-hint">
                                    Enter your 5-digit ZIP and click Look up to fill in city and state.
                                </p>
                                {fieldErrors.postalCode && (
                                    <span className="field-error">{fieldErrors.postalCode}</span>
                                )}
                                {zipLookupError && <span className="field-error">{zipLookupError}</span>}
                            </div>
                        )}

                        {SHOW_ATHLETE_PROFILE_ZIP_FIELD ? (
                            <div className="athlete-edit-location-readonly-grid">
                                <div className="athlete-edit-field">
                                    <span className="athlete-edit-readonly-label">City</span>
                                    <div
                                        className="athlete-edit-readonly-value"
                                        title={editData.cityName || undefined}
                                    >
                                        {editData.cityName?.trim()
                                            ? editData.cityName
                                            : "—"}
                                    </div>
                                </div>
                                <div className="athlete-edit-field">
                                    <span className="athlete-edit-readonly-label">State</span>
                                    <div
                                        className="athlete-edit-readonly-value"
                                        title={(editData.location || editData.stateName) || undefined}
                                    >
                                        {(editData.location || editData.stateName)?.trim()
                                            ? (editData.location || editData.stateName)
                                            : "—"}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="athlete-edit-location-readonly-grid">
                                <div className="athlete-edit-field">
                                    <StateComboBox
                                        label="State"
                                        value={editData.stateId}
                                        options={stateOptions}
                                        placeholder="Search for a state..."
                                        onChange={(stateId) => {
                                            const stateName = stateOptions.find((s) => s.id === stateId)?.name || "";
                                            setEditData((prev) => ({
                                                ...prev,
                                                stateId,
                                                location: stateName || undefined,
                                                stateName: stateName || undefined,
                                                cityId: undefined,
                                                cityName: "",
                                            }));
                                        }}
                                    />
                                </div>
                                <div className="athlete-edit-field">
                                    <CityComboBox
                                        label="City"
                                        value={selectedCity}
                                        stateId={editData.stateId}
                                        disabled={editData.stateId == null}
                                        placeholder={
                                            editData.stateId == null
                                                ? "Select a state first..."
                                                : "Search for a city..."
                                        }
                                        onChange={(city) => {
                                            setEditData((prev) => ({
                                                ...prev,
                                                cityId: city?.id,
                                                cityName: city?.name || "",
                                                stateId: city?.stateId ?? prev.stateId,
                                                location: city?.stateName || prev.location,
                                                stateName: city?.stateName || prev.stateName,
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Academic & Sport Section */}
                <div className="athlete-edit-section-content">
                    <div className="athlete-edit-section-header section-academic">
                        <span className="section-icon">🏆</span>
                        <span>Academic & Sport</span>
                    </div>

                    <div className="athlete-edit-field">
                        <label>SCHOOL</label>
                        <input
                            type="text"
                            value={editData.school || ''}
                            onChange={
                                isCleverAthlete
                                    ? undefined
                                    : (e) => handleFieldChange('school', e.target.value)
                            }
                            readOnly={isCleverAthlete}
                            disabled={isCleverAthlete}
                        />
                        {isCleverAthlete && (
                            <span className="field-note">
                                School name is set by your school subscription and cannot be changed.
                            </span>
                        )}
                    </div>

                    <div className="athlete-edit-field">
                        <label>GRADUATION YEAR</label>
                        <input
                            type="text"
                            value={editData.graduationYear || ''}
                            onChange={(e) => handleFieldChange('graduationYear', e.target.value)}
                        />
                    </div>

                    <div className="athlete-edit-field">
                        <ComboBox
                            label="SPORT"
                            value={typeof editData.sport === 'object' && editData.sport?.id ? editData.sport.id : undefined}
                            options={sports}
                            groupedOptions={sportGroups.length > 0 ? sportGroups : undefined}
                            onChange={(id) => {
                                if (!id) {
                                    setEditData(prev => ({ ...prev, sport: undefined }));
                                    return;
                                }
                                const selectedSport = sports.find(s => s.id === id);
                                if (selectedSport) {
                                    setEditData(prev => ({ ...prev, sport: selectedSport }));
                                }
                            }}
                            disabled={sportsLoading}
                            loading={sportsLoading}
                            error={sportsError ? "Failed to load sports" : undefined}
                            placeholder="Search for a sport..."
                            testId="athlete-sport-input"
                            noOptionsMessage="No sports found"
                            loadingMessage="Loading sports..."
                        />
                    </div>

                    <div className="athlete-edit-field">
                        <label>PROGRAM / CLUB</label>
                        <input
                            type="text"
                            value={editData.program || ''}
                            onChange={(e) => handleFieldChange('program', e.target.value)}
                            placeholder="Your club or program"
                        />
                    </div>
                </div>

                {/* Athlete Contact Section */}
                <div className="athlete-edit-section-content">
                    <div className="athlete-edit-section-header section-contact">
                        <span className="section-icon">📱</span>
                        <span>Athlete Contact</span>
                    </div>

                    <div className="athlete-edit-field">
                        <label>EMAIL*</label>
                        <input
                            type="email"
                            className={fieldErrors.email ? "input-error" : ""}
                            value={editData.email || ''}
                            onChange={(e) => handleFieldChange('email', e.target.value)}
                        />
                        {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
                    </div>

                    <div className="athlete-edit-field">
                        <label>MOBILE</label>
                        <input
                            type="tel"
                            value={editData.phone || editData.athletePhone || ''}
                            onChange={(e) => handleFieldChange('phone', e.target.value)}
                            placeholder="+1 (555) 000-0000"
                        />
                    </div>
                </div>

                {/* Parent / Guardian Contact Section */}
                <div className="athlete-edit-section-content">
                    <div className="athlete-edit-section-header section-parent">
                        <span className="section-icon">👥</span>
                        <span>Parent / Guardian Contact</span>
                    </div>

                    <div className="athlete-edit-field">
                        <label>EMAIL</label>
                        <input
                            type="email"
                            value={editData.parentEmail || ''}
                            disabled
                            readOnly
                        />
                    </div>

                    <div className="athlete-edit-field">
                        <label>EMAIL 2</label>
                        <input
                            type="email"
                            value={editData.parentEmail2 || ''}
                            disabled
                            readOnly
                        />
                    </div>

                    <div className="athlete-edit-field">
                        <label>MOBILE</label>
                        <input
                            type="tel"
                            value={editData.parentPhone || editData.parentMobile || ''}
                            onChange={(e) => handleFieldChange('parentPhone', e.target.value)}
                        />
                    </div>
                </div>

                {/* Delete Account Button */}
                {isAthlete() && (
                    <div className="athlete-edit-field athlete-edit-delete-field">
                        <button
                            type="button"
                            className="athlete-delete-account-btn"
                            onClick={() => setIsDeleteModalOpen(true)}
                            disabled={saving}
                            data-testid="delete-account-button"
                        >
                            Delete Account
                        </button>
                    </div>
                )}
            </div>

            {/* Profile Picture Upload Modal */}
            <UploadImageModal
                open={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                mode="deferred"
                onSelected={handleUploadProfilePicture}
                title="Upload profile picture"
            />

            {/* Delete Account Modal */}
            <AthleteDeleteAccountModal
                open={isDeleteModalOpen}
                onClose={() => {
                    if (!isDeleting) {
                        setIsDeleteModalOpen(false);
                        setDeleteError(null);
                    }
                }}
                onConfirm={handleDeleteAccount}
                loading={isDeleting}
                error={deleteError}
            />
        </div>
    );
}
